import React, { useEffect, useState } from "react";
import Footer from "../Component/Footer";
import Header from "../Component/Header";
import { ChevronRight, Users, Target, TrendingUp, Heart, Award, Globe, ArrowRight, Activity, Shield, Stethoscope, BookOpen, MapPin } from "lucide-react";

function Tufayo() {
  const [isVisible, setIsVisible] = useState({});
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('overview');

  const mainImages = [
    "img/tufayo/0-min.jpg",
    "img/tufayo/2-min.jpg",
    "img/tufayo/3-min.jpg"
  ];

  const galleryImages = [
    "img/tufayo/4-min.jpg",
    "img/tufayo/5-min.jpg",
    "img/tufayo/6-min.jpg",
    "img/tufayo/7-min.jpg",
    "img/tufayo/8-min.jpg",
    "img/tufayo/9-min.jpg"
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    const imageInterval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % mainImages.length);
    }, 4000);

    return () => {
      observer.disconnect();
      clearInterval(imageInterval);
    };
  }, []);

  const stats = [
    { icon: Activity, number: "253", label: "Cases per 100 Adults", description: "TB prevalence in Uganda (WHO 2015)", color: "from-red-500 to-orange-500" },
    { icon: Users, number: "36", label: "Cases per 1000 Children", description: "Pediatric TB burden", color: "from-blue-500 to-cyan-500" },
    { icon: MapPin, number: "Kayunga", label: "Primary Focus Area", description: "Town Council & sub-county", color: "from-green-500 to-emerald-500" },
    { icon: Globe, number: "Global Fund", label: "Funded Initiative", description: "Supervised by Baylor Uganda", color: "from-purple-500 to-pink-500" }
  ];

  const strategies = [
    {
      icon: Activity,
      title: "Community Screening",
      description: "Comprehensive TB screening programs in hotspot areas with immediate referral systems",
      impact: "Early Detection",
      color: "from-red-500 to-pink-500"
    },
    {
      icon: Stethoscope,
      title: "Radio Talk Shows",
      description: "Wide-reaching educational programs broadcast to communities across Kayunga",
      impact: "Mass Awareness",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: Users,
      title: "Community Dialogues",
      description: "Interactive sessions fostering direct engagement and knowledge transfer",
      impact: "Deep Engagement",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: BookOpen,
      title: "Capacity Building",
      description: "Training programs for leadership, governance, and resource mobilization",
      impact: "Organizational Excellence",
      color: "from-purple-500 to-pink-500"
    }
  ];

  const tabs = [
    { id: 'overview', label: 'Project Overview', icon: Globe },
    { id: 'approach', label: 'Our Approach', icon: Target },
    { id: 'impact', label: 'Impact & Results', icon: TrendingUp },
    { id: 'gallery', label: 'Project Gallery', icon: Heart }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
      <Header />

      {/* Hero Section */}
      <div className="relative overflow-hidden pt-20 sm:pt-32 pb-12 sm:pb-20">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-64 h-64 bg-blue-200/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-48 h-48 bg-cyan-200/30 rounded-full blur-2xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-green-200/30 rounded-full blur-xl animate-pulse delay-2000"></div>

          {/* Medical cross pattern */}
          <div className="absolute top-1/3 right-1/4 w-96 h-96 opacity-5">
            <svg viewBox="0 0 100 100" className="w-full h-full text-blue-600">
              <path d="M20,40 L20,60 L40,60 L40,80 L60,80 L60,60 L80,60 L80,40 L60,40 L60,20 L40,20 L40,40 Z" fill="currentColor" />
            </svg>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div
              id="hero-content"
              data-animate
              className={`transform transition-all duration-1000 mt-20 sm:mt-0 text-center sm:text-left px-4 sm:px-0 ${isVisible['hero-content'] ? 'translate-x-0 opacity-100' : 'lg:-translate-x-full opacity-0'
                }`}
            >
              <div className="inline-flex items-center bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <Stethoscope className="w-4 h-4 mr-2" />
                Global Fund Initiative
              </div>

              <h1 className="text-4xl md:text-6xl font-black mb-6">
                <span className="bg-gradient-to-r from-blue-600 via-cyan-600 to-green-600 bg-clip-text text-transparent">
                  Tufayo
                </span>
                <br />
                <span className="text-gray-800">TB Awareness Project</span>
              </h1>

              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-l-4 border-blue-500 p-6 rounded-r-2xl mb-8">
                <p className="text-lg text-gray-700 font-medium">
                  <span className="font-bold text-blue-800">"Tufayo"</span> means "We Care" in Luganda.
                  Our mission is to bridge the critical information gap that contributes to Uganda's
                  alarming TB prevalence rates.
                </p>
              </div>

              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                Funded by the Global Fund and supervised by Baylor Uganda, we're combating tuberculosis
                through comprehensive community awareness, screening, and capacity building programs.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center sm:justify-start">
                <button
                  onClick={() => window.location.href = '/contact-us'}
                  className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-8 py-4 rounded-2xl font-semibold text-lg transform hover:scale-105 transition-all duration-300 shadow-lg flex items-center justify-center gap-2"
                >
                  Partner With Us <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Image Carousel */}
            <div
              id="hero-image"
              data-animate
              className={`transform transition-all duration-1000 delay-300 ${isVisible['hero-image'] ? 'translate-x-0 opacity-100' : 'lg:translate-x-full opacity-0'
                }`}
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <div className="relative h-96 md:h-[500px]">
                  <img
                    src={mainImages[currentImageIndex]}
                    alt="Tufayo TB Awareness"
                    className="w-full h-full object-cover transition-opacity duration-1000"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>

                  {/* Overlay badge */}
                  <div className="absolute top-6 left-6 bg-white/90 backdrop-blur-sm rounded-2xl px-4 py-2">
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-blue-600" />
                      <span className="font-semibold text-gray-800">Global Fund Project</span>
                    </div>
                  </div>
                </div>

                {/* Image indicators */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                  {mainImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${index === currentImageIndex ? 'bg-white scale-125' : 'bg-white/50'
                        }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Section */}
      <div className="py-20 bg-white/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4">
          <div
            id="stats-header"
            data-animate
            className={`text-center mb-16 transform transition-all duration-1000 ${isVisible['stats-header'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              The TB <span className="bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">Crisis</span> in Uganda
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Understanding the scope of the challenge through WHO data and national statistics
            </p>
            <div className="w-32 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full mx-auto mt-6"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  id={`stat-${index}`}
                  data-animate
                  className={`transform transition-all duration-1000 delay-${index * 200} ${isVisible[`stat-${index}`] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                    }`}
                >
                  <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-l-4 border-blue-500">
                    <div className={`w-16 h-16 bg-gradient-to-r ${stat.color} rounded-2xl flex items-center justify-center mb-6 mx-auto`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-center">
                      <div className="text-4xl font-black text-gray-800 mb-2">{stat.number}</div>
                      <div className="text-lg font-semibold text-blue-600 mb-2">{stat.label}</div>
                      <div className="text-sm text-gray-600">{stat.description}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Interactive Tabs Section */}
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          {/* Tab Navigation */}
          <div className="flex flex-wrap justify-center mb-12">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-semibold text-lg transition-all duration-300 mx-2 mb-2 ${activeTab === tab.id
                      ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg transform scale-105'
                      : 'bg-white text-gray-600 hover:bg-gray-50 shadow'
                    }`}
                >
                  <Icon className="w-5 h-5" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          <div className="transition-all duration-500">
            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h3 className="text-3xl font-bold text-gray-800 mb-6">
                    Bridging the <span className="bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">Information Gap</span>
                  </h3>
                  <div className="space-y-6 text-lg text-gray-700">
                    <p>
                      The alarming TB statistics in Uganda—<strong>253 cases per 100 adults and 36 cases per 1000 children</strong>—
                      are primarily attributed to a critical information gap among the population.
                    </p>

                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-500 p-6 rounded-r-2xl">
                      <p className="font-semibold text-amber-800">
                        Our project specifically targets Kayunga Town Council and sub-county areas,
                        focusing on youth in identified hotspots where TB transmission rates are highest.
                      </p>
                    </div>

                    <p>
                      Through strategic partnerships with the Global Fund and Baylor Uganda,
                      we implement evidence-based interventions that address both immediate health needs
                      and long-term organizational capacity building.
                    </p>
                  </div>
                </div>
                <div className="relative">
                  <img
                    src="img/tufayo/0-min.jpg"
                    alt="Community engagement"
                    className="w-full h-80 object-cover rounded-3xl shadow-2xl"
                  />
                </div>
              </div>
            )}

            {activeTab === 'approach' && (
              <div>
                <h3 className="text-3xl font-bold text-gray-800 mb-12 text-center">
                  Our Multi-Faceted <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">Strategy</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {strategies.map((strategy, index) => {
                    const Icon = strategy.icon;
                    return (
                      <div key={index} className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                        <div className={`w-16 h-16 bg-gradient-to-r ${strategy.color} rounded-2xl flex items-center justify-center mb-6`}>
                          <Icon className="w-8 h-8 text-white" />
                        </div>
                        <h4 className="text-xl font-bold text-gray-800 mb-4">{strategy.title}</h4>
                        <p className="text-gray-600 mb-4">{strategy.description}</p>
                        <div className="inline-flex items-center bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                          <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                          {strategy.impact}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {activeTab === 'impact' && (
              <div className="text-center">
                <h3 className="text-3xl font-bold text-gray-800 mb-8">
                  Measurable <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Impact</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <div className="bg-gradient-to-br from-blue-600 to-cyan-600 rounded-3xl p-8 text-white">
                    <div className="text-4xl font-black mb-4">100%</div>
                    <div className="text-lg font-semibold mb-2">Referral Success</div>
                    <div className="text-blue-100">All presumptive cases referred for testing</div>
                  </div>
                  <div className="bg-gradient-to-br from-green-600 to-emerald-600 rounded-3xl p-8 text-white">
                    <div className="text-4xl font-black mb-4">24/7</div>
                    <div className="text-lg font-semibold mb-2">Radio Coverage</div>
                    <div className="text-green-100">Continuous community education</div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-3xl p-8 text-white">
                    <div className="text-4xl font-black mb-4">5+</div>
                    <div className="text-lg font-semibold mb-2">Capacity Areas</div>
                    <div className="text-purple-100">Leadership & governance training</div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'gallery' && (
              <div>
                <h3 className="text-3xl font-bold text-gray-800 mb-12 text-center">
                  Project <span className="bg-gradient-to-r from-pink-600 to-red-600 bg-clip-text text-transparent">Gallery</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {galleryImages.map((image, index) => (
                    <div key={index} className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                      <img
                        src={image}
                        alt={`Tufayo project activity ${index + 1}`}
                        className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="absolute bottom-4 left-4 text-white">
                          <div className="font-semibold">Community Engagement</div>
                          <div className="text-sm text-gray-200">Tufayo TB Awareness</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Partnership & Call to Action Section */}
      <div className="py-20 bg-gradient-to-r from-blue-600 via-cyan-600 to-green-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <div
            id="cta-content"
            data-animate
            className={`transform transition-all duration-1000 ${isVisible['cta-content'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}
          >
            <div className="inline-flex items-center bg-white/20 backdrop-blur-sm text-white px-6 py-3 rounded-full text-lg font-semibold mb-8">
              <Shield className="w-5 h-5 mr-3" />
              Global Fund Partnership • Baylor Uganda Supervision
            </div>

            <h2 className="text-4xl md:text-6xl font-bold text-white mb-8">
              Together We Can End TB
            </h2>
            <p className="text-xl text-white/90 mb-12 max-w-3xl mx-auto">
              Join us in our mission to eliminate tuberculosis in Uganda. Your support enables
              comprehensive screening, community education, and life-saving interventions.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button
                onClick={() => window.location.href = '/contact-us'}
                className="bg-white text-blue-600 px-10 py-4 rounded-2xl font-bold text-lg transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Partner With Us
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Tufayo;